import type { FC } from 'react';
import { useStoreState } from 'easy-peasy';
import { candidateSelector } from '@model/Candidate/Candidate.selectors';
import { SummaryDetailsCard } from '@components/Organisms/SummaryDetails/_partials/SummaryDetailsCard';
import { useTranslation } from 'react-i18next';
import { MOTIVATION_TO_CHANGE_TO_ICONS_MAP } from '@components/Organisms/SummaryDetails/_constants/SummaryDetails.constants';
import { Icon, ICON_NAMES } from '@reverse-hr/design-system';
import { GlobalCompensation } from '@components/Organisms/GlobalCompensation/index';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';
import { getCandidateCompensationIsVisible } from '@model/Candidate/_helpers/get-candidate-compensation-visibility';

export const SummaryDetails: FC = () => {
  const { t } = useTranslation('', {
    keyPrefix: 'candidate.sections.summary',
  });

  const { t: tShowcase } = useTranslation('', {
    keyPrefix: 'showcase',
  });

  const candidate = useStoreState(candidateSelector);
  const isCompensationVisible = getCandidateCompensationIsVisible(
    candidate?.compensationPackage ?? null,
  );
  const userPackageVisibility = useUserPackageVisibility();

  if (!candidate) {
    return null;
  }

  const motivationToChangeContent = candidate.motivationToChange
    ? tShowcase(`motivationToChangeValues.${candidate.motivationToChange}`)
    : null;

  const motivationToChangeIcon = candidate.motivationToChange
    ? MOTIVATION_TO_CHANGE_TO_ICONS_MAP[candidate.motivationToChange]
    : ICON_NAMES.CONFUSED;

  const activelySearchingContent =
    candidate.currentSelectionProcessesCount !== null
      ? tShowcase('otherSelections.inProgress', {
          count: candidate.currentSelectionProcessesCount,
        })
      : tShowcase('otherSelections.notSpecified');

  const selectionProcessesContent = candidate.isActivelySearching
    ? activelySearchingContent
    : tShowcase('no');

  return (
    <div className="c-summary-details">
      <SummaryDetailsCard title={tShowcase('tableRows.seniority')}>
        {candidate.seniority}
      </SummaryDetailsCard>

      {!!candidate.motivationToChange && (
        <SummaryDetailsCard
          className="c-summary-details-card--motivation-to-change"
          title={tShowcase('tableRows.motivation')}
        >
          <Icon name={motivationToChangeIcon} /> {motivationToChangeContent}
        </SummaryDetailsCard>
      )}

      <SummaryDetailsCard title={tShowcase('tableRows.notice_period')}>
        {candidate.noticePeriod}
      </SummaryDetailsCard>

      <SummaryDetailsCard
        className="c-summary-details-card--non-compete-agreement"
        title={tShowcase('tableRows.non_compete_agreement')}
      >
        {candidate.nonCompeteAgreement}
      </SummaryDetailsCard>

      <SummaryDetailsCard
        className="c-summary-details-card--active-searching"
        title={t('otherSelectionsTitle')}
      >
        {selectionProcessesContent}
      </SummaryDetailsCard>

      {!!candidate.compensationPackage?.currentAnnualSalary &&
        isCompensationVisible &&
        userPackageVisibility.isVisible && (
          <SummaryDetailsCard
            className="c-summary-details-card--global-compensation"
            title={tShowcase('tableRows.current_global_compensation')}
          >
            <GlobalCompensation candidate={candidate} />
          </SummaryDetailsCard>
        )}

      {!!candidate.compensationPackage?.desiredAnnualSalary &&
        isCompensationVisible &&
        userPackageVisibility.isVisible && (
          <SummaryDetailsCard
            className="c-summary-details-card--global-compensation"
            title={tShowcase('tableRows.desired_global_compensation')}
          >
            <GlobalCompensation candidate={candidate} isDesired />
          </SummaryDetailsCard>
        )}
    </div>
  );
};
